import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import Lottie from "lottie-react";
import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { all_routes } from "../router/all_routes";

import {
  BookingSteps,
  formatToTwoDecimals,
} from "../../core/data/redux/actions/commonActions";

import { getBooking } from "../../core/data/redux/action";
import { RootState } from "../../core/data/redux/store";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";
import Loader from "../../core/data/img/car_loading.json";
import type { AppDispatch } from "../../core/data/redux/store";

const BookingSuccess = () => {
  const API = axios.create({ baseURL: process.env.REACT_APP_API_URL });
  const dispatch = useDispatch<AppDispatch>();
  const routes = all_routes;

  const { bookingDetailLoading, bookingDetail, paymentDetails } = useSelector(
    (state: RootState) => ({
      paymentDetails: state.stripe.paymentDetails,
      bookingDetail: state.booking.bookingDetail,
      bookingDetailLoading: state.booking.bookingDetailLoading,
    })
  );

  const [loading, setLoading] = useState(bookingDetailLoading);
  const [step] = useState(5);

  useEffect(() => {
    if (paymentDetails) {
      if (paymentDetails?.booking_id) {
        dispatch(getBooking(paymentDetails?.booking_id));
      }
    }
  }, [paymentDetails]);

  const generateInvoice = async () => {
    setLoading(true);
    window.scrollTo(0, 0);
    try {
      const response = await API.post(
        "/generate-invoice",
        { booking_id: bookingDetail?.id },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          responseType: "blob",
        }
      );

      if (response.data) {
        setLoading(false);
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Invoice_${bookingDetail?.booking_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setLoading(false);
        console.error("The response data is not a valid Blob.");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error generating the invoice:", error);
    }
  };

  return (
    <div>
      {loading ? (
        <div className="d-flex justify-content-center align-items-center">
          <Lottie
            animationData={Loader}
            loop={true}
            style={{ width: 300, height: 300 }}
          />
        </div>
      ) : (
        <div className="booking-new-module">
          <div className="container p-0">
            <div className="booking-wizard-head mb-0">
              <div className="row align-items-center">
                <div className="col-xl-4 col-lg-3">
                  <div className="booking-head-title d-none d-md-block">
                    <h4>Reserve Your Car</h4>
                  </div>
                </div>
                <div className="col-xl-8 col-lg-9 justify-content-left display-grid">
                  <Box className="px-0 " sx={{ width: "100%" }}>
                    <Stepper
                      activeStep={step}
                      alternativeLabel
                      sx={{
                        ".MuiStep-root": { width: "5rem" },
                        ".MuiStepIcon-root.Mui-completed": { color: "#009FDF" },
                        ".MuiStepIcon-root.Mui-active": { color: "#009FDF" },
                        ".MuiStepLabel-label": {
                          fontFamily: "GT Walsheim Trial Lt",
                        },
                      }}
                    >
                      {BookingSteps?.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                </div>
              </div>
            </div>
            <div className="booking-card mb-0">
              <div className="success-book">
                <span className="success-icon">
                  <i className="fa-solid fa-check-double" />
                </span>
                <h6 className="fw-bold">
                  Thank you! Your Order has been Recieved
                </h6>
                <h6 className="order-no">
                  Order Number : <span>#{bookingDetail?.booking_id}</span>
                </h6>
              </div>
              <div className="booking-header">
                <div className="booking-img-wrap">
                  <div className="book-img">
                    <ImageWithBasePath
                      src={bookingDetail?.vehicle_image}
                      alt="img"
                    />
                  </div>
                  <div className="book-info">
                    <h6>
                      {bookingDetail?.vehicle?.year_make_model ||
                        bookingDetail?.vehicle_name}
                    </h6>
                    <p>
                      <i className="feather icon-map-pin" /> Location :{" "}
                      {bookingDetail?.pickup_location}
                    </p>
                  </div>
                </div>
              </div>
              <div className="row">
                {/* Car Pricing */}
                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="book-card flex-fill">
                    <div className="book-head d-flex align-items-center justify-content-between">
                      <h6 className="fw-bold">Upfront payment</h6>

                      <p className="mb-0">
                        $
                        {formatToTwoDecimals(
                          bookingDetail?.cost_summary?.initialPayment
                        )}{" "}
                      </p>
                    </div>
                    <div className="book-body">
                      <ul className="pricing-lists">
                        <li>
                          <p>
                            Rental Cost (
                            {bookingDetail?.cost_summary?.daysCount ?? 7} Days @
                            $
                            {formatToTwoDecimals(
                              bookingDetail?.cost_summary?.pricePerDay ?? 1
                            )}
                            /day){" "}
                          </p>
                          <span>
                            ${" "}
                            {formatToTwoDecimals(
                              bookingDetail?.cost_summary?.initialRentalCost ??
                                0
                            )}
                          </span>
                        </li>
                        {bookingDetail?.cost_summary?.feeTypes?.map(
                          (item: any, index: any) => (
                            <li key={index}>
                              <p>{item.fee_type}</p>
                              <span>
                                ${formatToTwoDecimals(item.calcAmount)}
                              </span>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="book-card flex-fill">
                    <div className="book-head">
                      <h6 className="fw-bold">Location &amp; Time</h6>
                    </div>
                    <div className="book-body">
                      <ul className="location-lists">
                        <li>
                          <h6>Pickup Location</h6>
                          <p>{bookingDetail?.pickup_location}</p>
                        </li>

                        <li>
                          <h6>Trip Date</h6>
                          <p>
                            {`${dayjs(bookingDetail?.booking_start_date).format("MMM DD")} ${bookingDetail?.booking_start_time} - ${dayjs(bookingDetail?.booking_return_date).format("MMM DD")} ${bookingDetail?.booking_return_time}`}
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="book-card flex-fill">
                    <div className="book-body">
                      {bookingDetail?.cost_summary?.nextBillingCycle &&
                        (bookingDetail?.cost_summary?.paymentMethod?.id?.toString() ===
                          "1" ||
                          bookingDetail?.cost_summary?.paymentMethod?.id?.toString() ===
                            "2") && (
                          <p>
                            {bookingDetail?.cost_summary?.paymentMethod?.id?.toString() ===
                              "1" &&
                            bookingDetail?.cost_summary?.nextBillingCycle
                              ?.payment_amount ? (
                              <>
                                {" "}
                                Your next daily payment of{" "}
                                <span className="fw-bold">
                                  {" "}
                                  $
                                  {formatToTwoDecimals(
                                    bookingDetail?.cost_summary
                                      ?.nextBillingCycle?.payment_amount
                                  )}
                                </span>{" "}
                                is due starting{" "}
                                {dayjs(
                                  bookingDetail?.booking_start_date
                                ).format("MMM DD")}{" "}
                                until{" "}
                                {dayjs(
                                  bookingDetail?.booking_return_date
                                ).format("MMM DD")}
                              </>
                            ) : null}

                            {bookingDetail?.cost_summary?.paymentMethod?.id?.toString() ===
                              "2" &&
                            bookingDetail?.nextBillingCycle?.payment_amount ? (
                              <>
                                {" "}
                                Your next weekly payment of{" "}
                                <span className="fw-bold">
                                  {" "}
                                  $
                                  {formatToTwoDecimals(
                                    bookingDetail?.cost_summary
                                      ?.nextBillingCycle?.payment_amount
                                  )}
                                </span>{" "}
                                is due starting{" "}
                                {dayjs(
                                  bookingDetail?.booking_start_date
                                ).format("MMM DD")}{" "}
                                until{" "}
                                {dayjs(
                                  bookingDetail?.booking_return_daternDate
                                ).format("MMM DD")}
                              </>
                            ) : (
                              ""
                            )}
                          </p>
                        )}
                    </div>
                  </div>
                </div>

                <div className="col-12  print-btn text-center d-flex gap-2 justify-content-center">
                  {bookingDetail?.booking_id && (
                    <Link
                      to="#"
                      onClick={() => {
                        generateInvoice();
                      }}
                      className="btn btn-secondary border-0"
                    >
                      Download Invoice
                    </Link>
                  )}

                  <Link
                    to={routes.homeOne}
                    className="btn btn-primary text-white border-0"
                  >
                    Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingSuccess;
